/**
 * WorkOrderDetailPage — Deep Data full detail/edit view
 *
 * Reached via /work-orders/:id route. Shows complete WO info
 * with editable fields, cost items, photos, activity timeline,
 * and status transition buttons.
 */

import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
	Button,
	Tag,
	Input,
	Select,
	Spin,
	message,
	Divider,
	Timeline,
	Table,
	Popconfirm,
	Modal,
	InputNumber,
	DatePicker,
} from "antd";
import dayjs from "dayjs";
import {
	ArrowLeftOutlined,
	WarningOutlined,
	PlusOutlined,
	DeleteOutlined,
	SendOutlined,
	DollarOutlined,
	ScanOutlined,
	BookOutlined,
	BulbOutlined,
	MessageOutlined,
	EditOutlined,
	CheckOutlined,
	CloseOutlined,
} from "@ant-design/icons";
import {
	getWorkOrder,
	updateWorkOrder,
	updateWorkOrderStatus,
	assignWorkOrder,
	listVendors,
	addComment as apiAddComment,
	addCostItem as apiAddCostItem,
	deleteCostItem as apiDeleteCostItem,
	createQuoteRequest,
	getQuoteRequestsByWorkOrder,
	submitVendorQuote,
	acceptQuote,
	cancelQuoteRequest as apiCancelQuoteRequest,
	type WorkOrder,
	type WorkOrderStatus,
	type WorkOrderPriority,
	type CostItem,
	type CostItemType,
	type Vendor,
	type QuoteRequestWithQuotes,
	type VendorQuoteWithVendor,
} from "@/services/work-order-service";
import {
	listMyVendors,
	type OrgVendorWithDetails,
} from "@/services/facility-link-service";
import { getOrganizationUsers } from "@/services/user-service";
import {
	flGetScansByWorkOrder,
	flGetMessages,
	flSendMessage,
	flSuggestQuestions,
	type FacilityLensScan,
	type FacilityLensMessage,
} from "@/services/facility-lens-service";
import {
	getTriageSessionsByWorkOrder,
	type TriageSessionListItem,
} from "@/services/facility-solve-triage-service";
import { Collapse, Checkbox } from "antd";

const { TextArea } = Input;

const STATUS_COLORS: Record<string, string> = {
	OPEN: "blue", ASSIGNED: "cyan", IN_PROGRESS: "processing",
	ON_HOLD: "warning", COMPLETED: "success", CLOSED: "default", CANCELLED: "error", DRAFT: "default",
};

const PRIORITY_LABELS: Record<number, string> = { 1: "Emergency", 2: "Urgent", 3: "High", 4: "Medium", 5: "Low" };

const NEXT_STATUS: Record<string, { status: WorkOrderStatus; label: string; danger?: boolean }[]> = {
	OPEN: [{ status: "ASSIGNED", label: "Assign" }, { status: "CANCELLED", label: "Cancel", danger: true }],
	ASSIGNED: [{ status: "IN_PROGRESS", label: "Start Work" }, { status: "OPEN", label: "Unassign" }, { status: "CANCELLED", label: "Cancel", danger: true }],
	IN_PROGRESS: [{ status: "COMPLETED", label: "Complete" }, { status: "ON_HOLD", label: "Hold" }],
	ON_HOLD: [{ status: "IN_PROGRESS", label: "Resume" }, { status: "CANCELLED", label: "Cancel", danger: true }],
	COMPLETED: [{ status: "CLOSED", label: "Close" }, { status: "IN_PROGRESS", label: "Reopen" }],
};

const PRIORITY_COLORS: Record<number, string> = { 1: "#ff4d4f", 2: "#fa8c16", 3: "#faad14", 4: "#1890ff", 5: "#8c8c8c" };

const WorkOrderDetailPage = () => {
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const [wo, setWo] = useState<WorkOrder | null>(null);
	const [loading, setLoading] = useState(true);
	const [comment, setComment] = useState("");
	const [submitting, setSubmitting] = useState(false);
	const [showCostModal, setShowCostModal] = useState(false);
	const [costForm, setCostForm] = useState({ costType: "LABOR" as CostItemType, description: "", totalCost: 0, hours: 0 });

	// Assignment modal state
	const [showAssignModal, setShowAssignModal] = useState(false);
	const [assignType, setAssignType] = useState<"user" | "vendor" | "fl_vendor">("user");
	const [assignUserId, setAssignUserId] = useState<number | null>(null);
	const [assignVendorId, setAssignVendorId] = useState<number | null>(null);
	const [assignFlOrgVendorId, setAssignFlOrgVendorId] = useState<number | null>(null);
	const [orgUsers, setOrgUsers] = useState<{ id: number; label: string; email: string }[]>([]);
	const [orgVendors, setOrgVendors] = useState<Vendor[]>([]);
	const [flVendors, setFlVendors] = useState<OrgVendorWithDetails[]>([]);
	const [loadingAssignees, setLoadingAssignees] = useState(false);

	// Triage sessions linked to this WO
	const [triageSessions, setTriageSessions] = useState<TriageSessionListItem[]>([]);

	// Quote request state
	const [quoteRequests, setQuoteRequests] = useState<QuoteRequestWithQuotes[]>([]);
	const [showQuoteModal, setShowQuoteModal] = useState(false);
	const [quoteForm, setQuoteForm] = useState({ title: "", description: "", scopeOfWork: "", deadline: "", vendorOrgIds: [] as number[] });
	const [loadingQuotes, setLoadingQuotes] = useState(false);
	const [showSubmitQuoteModal, setShowSubmitQuoteModal] = useState(false);
	const [submitQuoteTarget, setSubmitQuoteTarget] = useState<VendorQuoteWithVendor | null>(null);
	const [submitQuoteForm, setSubmitQuoteForm] = useState({ quotedAmount: 0, estimatedHours: 0, estimatedDays: 0, proposalNotes: "" });

	// Completion modal state
	const [showCompleteModal, setShowCompleteModal] = useState(false);
	const [completionForm, setCompletionForm] = useState({ rootCause: "", resolutionNotes: "" });

	// Inline editing state
	const [editingField, setEditingField] = useState<string | null>(null);
	const [editValues, setEditValues] = useState<Record<string, any>>({});

	// FacilityLens state
	const [flScans, setFlScans] = useState<FacilityLensScan[]>([]);
	const [flMessages, setFlMessages] = useState<FacilityLensMessage[]>([]);
	const [flMsgInput, setFlMsgInput] = useState("");
	const [flSending, setFlSending] = useState(false);
	const [flSuggestions, setFlSuggestions] = useState<{ question: string; options?: string[] }[]>([]);

	const fetchDetail = useCallback(async () => {
		if (!id) return;
		setLoading(true);
		try {
			const data = await getWorkOrder(Number(id));
			setWo(data);
		} catch {
			message.error("Failed to load work order");
		} finally {
			setLoading(false);
		}
	}, [id]);

	useEffect(() => { fetchDetail(); }, [fetchDetail]);

	// Load FacilityLens scans when WO loads
	useEffect(() => {
		if (!id) return;
		flGetScansByWorkOrder(Number(id))
			.then((scans) => {
				setFlScans(scans);
				if (scans.length > 0) {
					// Load messages for the first scan
					flGetMessages(scans[0].id).then(setFlMessages).catch(() => {});
					// Load suggested questions
					flSuggestQuestions(scans[0].id).then(setFlSuggestions).catch(() => {});
				}
			})
			.catch(() => {}); // Non-critical: WO may have no scans
	}, [id]);

	const handleFlSend = async () => {
		if (!flMsgInput.trim() || flScans.length === 0) return;
		setFlSending(true);
		try {
			const msg = await flSendMessage(flScans[0].id, flMsgInput.trim(), "TECHNICIAN");
			setFlMessages((prev) => [...prev, msg]);
			setFlMsgInput("");
		} catch {
			message.error("Failed to send message");
		} finally {
			setFlSending(false);
		}
	};

	const openAssignModal = async () => {
		setShowAssignModal(true);
		if (orgUsers.length === 0) {
			setLoadingAssignees(true);
			try {
				const [userData, vendorData, flData] = await Promise.all([
					getOrganizationUsers().catch(() => []),
					listVendors().catch(() => ({ vendors: [] })),
					listMyVendors().catch(() => []),
				]);
				setOrgUsers((userData || []).map((u: any) => ({
					id: u.id,
					label: `${u.first_name || u.firstName || ""} ${u.last_name || u.lastName || ""}`.trim() || u.email,
					email: u.email,
				})));
				setOrgVendors(vendorData.vendors || []);
				setFlVendors(flData || []);
			} finally {
				setLoadingAssignees(false);
			}
		}
	};

	const handleAssign = async () => {
		if (!wo) return;
		setSubmitting(true);
		try {
			await assignWorkOrder(
				wo.id,
				assignType === "user" ? assignUserId : null,
				assignType === "vendor" ? assignVendorId : null,
				assignType === "fl_vendor" ? assignFlOrgVendorId : null,
			);
			message.success("Work order assigned");
			setShowAssignModal(false);
			await fetchDetail();
		} catch (error: any) {
			message.error(error.message || "Failed to assign");
		} finally {
			setSubmitting(false);
		}
	};

	const handleStatus = async (newStatus: WorkOrderStatus) => {
		if (!wo) return;
		if (newStatus === "ASSIGNED") {
			openAssignModal();
			return;
		}
		if (newStatus === "COMPLETED") {
			setCompletionForm({ rootCause: wo.rootCause ?? "", resolutionNotes: wo.resolutionNotes ?? "" });
			setShowCompleteModal(true);
			return;
		}
		setSubmitting(true);
		try {
			await updateWorkOrderStatus(wo.id, newStatus);
			message.success(`Status updated to ${newStatus.replace(/_/g, " ")}`);
			await fetchDetail();
		} catch (error: any) {
			message.error(error.message || "Failed");
		} finally {
			setSubmitting(false);
		}
	};

	const handleCompleteSubmit = async () => {
		if (!wo) return;
		if (!completionForm.resolutionNotes.trim()) {
			message.warning("Please describe the resolution before completing");
			return;
		}
		setSubmitting(true);
		try {
			await updateWorkOrder(wo.id, {
				rootCause: completionForm.rootCause.trim() || undefined,
				resolutionNotes: completionForm.resolutionNotes.trim(),
			});
			await updateWorkOrderStatus(wo.id, "COMPLETED");
			message.success("Work order completed");
			setShowCompleteModal(false);
			await fetchDetail();
		} catch (error: any) {
			message.error(error.message || "Failed to complete");
		} finally {
			setSubmitting(false);
		}
	};

	const startEditing = (field: string, currentValue: any) => {
		setEditingField(field);
		setEditValues({ ...editValues, [field]: currentValue });
	};

	const cancelEditing = () => {
		setEditingField(null);
	};

	const saveField = async (field: string) => {
		if (!wo) return;
		try {
			await updateWorkOrder(wo.id, { [field]: editValues[field] });
			message.success("Updated");
			setEditingField(null);
			await fetchDetail();
		} catch (error: any) {
			message.error(error.message || "Failed to update");
		}
	};

	const handleComment = async () => {
		if (!comment.trim() || !wo) return;
		setSubmitting(true);
		try {
			await apiAddComment(wo.id, comment.trim());
			setComment("");
			await fetchDetail();
		} catch {
			message.error("Failed to add comment");
		} finally {
			setSubmitting(false);
		}
	};

	const handleAddCost = async () => {
		if (!wo || !costForm.totalCost) return;
		try {
			await apiAddCostItem(wo.id, {
				costType: costForm.costType,
				description: costForm.description || undefined,
				totalCost: costForm.totalCost,
				hours: costForm.hours || undefined,
			});
			message.success("Cost item added");
			setShowCostModal(false);
			setCostForm({ costType: "LABOR", description: "", totalCost: 0, hours: 0 });
			await fetchDetail();
		} catch {
			message.error("Failed to add cost");
		}
	};

	const handleDeleteCost = async (costId: number) => {
		if (!wo) return;
		try {
			await apiDeleteCostItem(wo.id, costId);
			message.success("Cost removed");
			await fetchDetail();
		} catch {
			message.error("Failed to remove cost");
		}
	};

	// Load quote requests when WO loads
	useEffect(() => {
		if (!id) return;
		setLoadingQuotes(true);
		getQuoteRequestsByWorkOrder(Number(id))
			.then(setQuoteRequests)
			.catch(() => {})
			.finally(() => setLoadingQuotes(false));
	}, [id]);

	// Load linked triage sessions
	useEffect(() => {
		if (!id) return;
		getTriageSessionsByWorkOrder(Number(id))
			.then(setTriageSessions)
			.catch(() => {});
	}, [id]);

	const fetchQuoteRequests = async () => {
		if (!id) return;
		const data = await getQuoteRequestsByWorkOrder(Number(id));
		setQuoteRequests(data);
	};

	const handleCreateQuoteRequest = async () => {
		if (!wo || !quoteForm.title.trim() || !quoteForm.vendorOrgIds.length) {
			message.warning("Please provide a title and select at least one vendor");
			return;
		}
		setSubmitting(true);
		try {
			await createQuoteRequest({
				workOrderId: wo.id,
				title: quoteForm.title.trim(),
				description: quoteForm.description.trim() || undefined,
				scopeOfWork: quoteForm.scopeOfWork.trim() || undefined,
				deadline: quoteForm.deadline || undefined,
				vendorOrgIds: quoteForm.vendorOrgIds,
			});
			message.success("Quote request sent to vendors");
			setShowQuoteModal(false);
			setQuoteForm({ title: "", description: "", scopeOfWork: "", deadline: "", vendorOrgIds: [] });
			await fetchQuoteRequests();
			await fetchDetail();
		} catch (error: any) {
			message.error(error.message || "Failed to create quote request");
		} finally {
			setSubmitting(false);
		}
	};

	const openSubmitQuoteModal = (quote: VendorQuoteWithVendor) => {
		setSubmitQuoteTarget(quote);
		setSubmitQuoteForm({ quotedAmount: Number(quote.quotedAmount) || 0, estimatedHours: Number(quote.estimatedHours) || 0, estimatedDays: Number(quote.estimatedDays) || 0, proposalNotes: quote.proposalNotes ?? "" });
		setShowSubmitQuoteModal(true);
	};

	const handleSubmitQuote = async () => {
		if (!submitQuoteTarget || !submitQuoteForm.quotedAmount) {
			message.warning("Please enter a quoted amount");
			return;
		}
		setSubmitting(true);
		try {
			await submitVendorQuote(submitQuoteTarget.id, {
				quotedAmount: submitQuoteForm.quotedAmount,
				estimatedHours: submitQuoteForm.estimatedHours || undefined,
				estimatedDays: submitQuoteForm.estimatedDays || undefined,
				proposalNotes: submitQuoteForm.proposalNotes.trim() || undefined,
			});
			message.success("Quote submitted");
			setShowSubmitQuoteModal(false);
			await fetchQuoteRequests();
		} catch (error: any) {
			message.error(error.message || "Failed to submit quote");
		} finally {
			setSubmitting(false);
		}
	};

	const handleAcceptQuote = async (requestId: number, quoteId: number) => {
		setSubmitting(true);
		try {
			await acceptQuote(requestId, quoteId);
			message.success("Quote accepted — vendor assigned to work order");
			await fetchQuoteRequests();
			await fetchDetail();
		} catch (error: any) {
			message.error(error.message || "Failed to accept quote");
		} finally {
			setSubmitting(false);
		}
	};

	const handleCancelQuoteRequest = async (requestId: number) => {
		setSubmitting(true);
		try {
			await apiCancelQuoteRequest(requestId);
			message.success("Quote request cancelled");
			await fetchQuoteRequests();
			await fetchDetail();
		} catch (error: any) {
			message.error(error.message || "Failed to cancel");
		} finally {
			setSubmitting(false);
		}
	};

	const openQuoteModal = async () => {
		if (flVendors.length === 0) {
			try {
				const data = await listMyVendors();
				setFlVendors(data || []);
			} catch { /* ignore */ }
		}
		if (wo) {
			setQuoteForm({ title: wo.title, description: wo.description ?? "", scopeOfWork: "", deadline: "", vendorOrgIds: [] });
		}
		setShowQuoteModal(true);
	};

	if (loading || !wo) {
		return (
			<div style={{ display: "flex", justifyContent: "center", padding: 80 }}>
				<Spin size="large" />
			</div>
		);
	}

	const nextActions = NEXT_STATUS[wo.status] ?? [];
	const formatDate = (d: string | null) => d ? new Date(d).toLocaleString() : "—";

	return (
		<div style={{ padding: 20, maxWidth: 960, margin: "0 auto" }}>
			<Button icon={<ArrowLeftOutlined />} onClick={() => navigate("/work-orders")} style={{ marginBottom: 16 }}>
				Back to Work Orders
			</Button>

			{/* Header */}
			<div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
				<div>
					<div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 4 }}>
						<span style={{ fontFamily: "monospace", fontSize: 14, opacity: 0.6 }}>{wo.workOrderNumber}</span>
						<Tag color={STATUS_COLORS[wo.status]}>{wo.status.replace(/_/g, " ")}</Tag>
						{wo.isSlaBreached && <Tag color="error" icon={<WarningOutlined />}>SLA BREACHED</Tag>}
					</div>
					<h1 style={{ margin: 0, fontSize: 24, fontWeight: 700 }}>{wo.title}</h1>
					<div style={{ display: "flex", gap: 8, marginTop: 8 }}>
						<Tag color={wo.priority <= 2 ? "red" : wo.priority === 3 ? "gold" : "blue"}>
							P{wo.priority} {PRIORITY_LABELS[wo.priority]}
						</Tag>
						<Tag>{wo.workType}</Tag>
						<Tag>{wo.source}</Tag>
					</div>
				</div>
			<div style={{ display: "flex", gap: 8 }}>
				{nextActions.map((a) =>
					a.danger ? (
						<Popconfirm key={a.status} title={`Are you sure you want to ${a.label.toLowerCase()} this work order?`} onConfirm={() => handleStatus(a.status)} okText="Yes" cancelText="No">
							<Button danger loading={submitting}>{a.label}</Button>
						</Popconfirm>
					) : (
						<Button key={a.status} type={a.status === "COMPLETED" ? "primary" : undefined} loading={submitting} onClick={() => handleStatus(a.status)}>
							{a.label}
						</Button>
					),
				)}
			</div>
			</div>

			{/* Info Grid */}
			<div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16, marginBottom: 20, padding: 16, background: "rgba(255,255,255,0.03)", borderRadius: 10, border: "1px solid rgba(255,255,255,0.06)" }}>
				<div><div style={{ fontSize: 11, opacity: 0.5, textTransform: "uppercase" }}>Location</div><div style={{ fontWeight: 500 }}>{wo.locationName ?? "—"}</div></div>
				<div><div style={{ fontSize: 11, opacity: 0.5, textTransform: "uppercase" }}>Asset</div><div style={{ fontWeight: 500 }}>{wo.assetTag ?? "N/A"}</div></div>
				<div><div style={{ fontSize: 11, opacity: 0.5, textTransform: "uppercase" }}>Assigned To</div><div style={{ fontWeight: 500 }}>{wo.assignedUserName ?? wo.assignedVendorName ?? "Unassigned"}</div></div>
				<div><div style={{ fontSize: 11, opacity: 0.5, textTransform: "uppercase" }}>Created By</div><div style={{ fontWeight: 500 }}>{wo.createdByUserName ?? "—"}</div></div>
				<div><div style={{ fontSize: 11, opacity: 0.5, textTransform: "uppercase" }}>SLA Deadline</div><div style={{ fontWeight: 500 }}>{formatDate(wo.slaDeadline)}</div></div>
				<div>
					<div style={{ fontSize: 11, opacity: 0.5, textTransform: "uppercase" }}>Due Date {!["CLOSED", "CANCELLED"].includes(wo.status) && <EditOutlined style={{ cursor: "pointer", marginLeft: 4 }} onClick={() => startEditing("dueDate", wo.dueDate)} />}</div>
					{editingField === "dueDate" ? (
						<div style={{ display: "flex", gap: 4, alignItems: "center" }}>
							<DatePicker
								size="small"
								value={editValues.dueDate ? dayjs(editValues.dueDate) : null}
								onChange={(d) => setEditValues({ ...editValues, dueDate: d?.toISOString() ?? null })}
								format="MMM D, YYYY"
							/>
							<Button type="text" size="small" icon={<CheckOutlined />} onClick={() => saveField("dueDate")} />
							<Button type="text" size="small" icon={<CloseOutlined />} onClick={cancelEditing} />
						</div>
					) : (
						<div style={{ fontWeight: 500 }}>{formatDate(wo.dueDate)}</div>
					)}
				</div>
				<div><div style={{ fontSize: 11, opacity: 0.5, textTransform: "uppercase" }}>Estimated Cost</div><div style={{ fontWeight: 500 }}>{wo.estimatedCost ? `$${Number(wo.estimatedCost).toLocaleString()}` : "—"}</div></div>
				<div><div style={{ fontSize: 11, opacity: 0.5, textTransform: "uppercase" }}>Actual Cost</div><div style={{ fontWeight: 600, color: "#92278f" }}>${Number(wo.actualCost).toLocaleString()}</div></div>
				<div><div style={{ fontSize: 11, opacity: 0.5, textTransform: "uppercase" }}>Labor Hours</div><div style={{ fontWeight: 500 }}>{wo.actualLaborHours ?? 0}h</div></div>
				<div>
					<div style={{ fontSize: 11, opacity: 0.5, textTransform: "uppercase" }}>
						Priority {!["CLOSED", "CANCELLED"].includes(wo.status) && <EditOutlined style={{ cursor: "pointer", marginLeft: 4 }} onClick={() => startEditing("priority", wo.priority)} />}
					</div>
					{editingField === "priority" ? (
						<div style={{ display: "flex", gap: 4, alignItems: "center" }}>
							<Select
								size="small"
								value={editValues.priority}
								onChange={(v) => setEditValues({ ...editValues, priority: v })}
								style={{ width: 140 }}
							>
								{([1, 2, 3, 4, 5] as WorkOrderPriority[]).map((p) => (
									<Select.Option key={p} value={p}>
										<span style={{ color: PRIORITY_COLORS[p], fontWeight: 600 }}>P{p} {PRIORITY_LABELS[p]}</span>
									</Select.Option>
								))}
							</Select>
							<Button type="text" size="small" icon={<CheckOutlined />} onClick={() => saveField("priority")} />
							<Button type="text" size="small" icon={<CloseOutlined />} onClick={cancelEditing} />
						</div>
					) : (
						<div style={{ fontWeight: 500 }}>
							<Tag color={wo.priority <= 2 ? "red" : wo.priority === 3 ? "gold" : "blue"}>
								P{wo.priority} {PRIORITY_LABELS[wo.priority]}
							</Tag>
						</div>
					)}
				</div>
				{wo.startedAt && <div><div style={{ fontSize: 11, opacity: 0.5, textTransform: "uppercase" }}>Started At</div><div style={{ fontWeight: 500 }}>{formatDate(wo.startedAt)}</div></div>}
				{wo.completedAt && <div><div style={{ fontSize: 11, opacity: 0.5, textTransform: "uppercase" }}>Completed At</div><div style={{ fontWeight: 500 }}>{formatDate(wo.completedAt)}</div></div>}
			</div>

			{/* Description (editable) */}
			<div style={{ padding: 12, background: "rgba(255,255,255,0.03)", borderRadius: 8, marginBottom: 20, border: "1px solid rgba(255,255,255,0.06)" }}>
				<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
					<span style={{ fontSize: 11, opacity: 0.5, textTransform: "uppercase", fontWeight: 600 }}>Description</span>
					{!["CLOSED", "CANCELLED"].includes(wo.status) && editingField !== "description" && (
						<Button type="text" size="small" icon={<EditOutlined />} onClick={() => startEditing("description", wo.description ?? "")} />
					)}
				</div>
				{editingField === "description" ? (
					<div>
						<TextArea
							value={editValues.description}
							onChange={(e) => setEditValues({ ...editValues, description: e.target.value })}
							rows={3}
							maxLength={2000}
						/>
						<div style={{ display: "flex", gap: 8, marginTop: 8, justifyContent: "flex-end" }}>
							<Button size="small" onClick={cancelEditing}>Cancel</Button>
							<Button size="small" type="primary" onClick={() => saveField("description")}>Save</Button>
						</div>
					</div>
				) : (
					<p style={{ margin: 0, whiteSpace: "pre-wrap" }}>{wo.description || <span style={{ opacity: 0.4 }}>No description provided</span>}</p>
				)}
			</div>

			{/* Root Cause & Resolution Notes */}
			{(wo.rootCause || wo.resolutionNotes || ["COMPLETED", "CLOSED"].includes(wo.status)) && (
				<div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 20 }}>
					<div style={{ padding: 12, background: "rgba(255,255,255,0.03)", borderRadius: 8, border: "1px solid rgba(255,255,255,0.06)" }}>
						<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
							<span style={{ fontSize: 11, opacity: 0.5, textTransform: "uppercase", fontWeight: 600 }}>Root Cause</span>
							{!["CLOSED", "CANCELLED"].includes(wo.status) && editingField !== "rootCause" && (
								<Button type="text" size="small" icon={<EditOutlined />} onClick={() => startEditing("rootCause", wo.rootCause ?? "")} />
							)}
						</div>
						{editingField === "rootCause" ? (
							<div>
								<TextArea
									value={editValues.rootCause}
									onChange={(e) => setEditValues({ ...editValues, rootCause: e.target.value })}
									rows={3}
									placeholder="What caused the issue?"
								/>
								<div style={{ display: "flex", gap: 8, marginTop: 8, justifyContent: "flex-end" }}>
									<Button size="small" onClick={cancelEditing}>Cancel</Button>
									<Button size="small" type="primary" onClick={() => saveField("rootCause")}>Save</Button>
								</div>
							</div>
						) : (
							<p style={{ margin: 0, whiteSpace: "pre-wrap" }}>{wo.rootCause || <span style={{ opacity: 0.4 }}>Not documented</span>}</p>
						)}
					</div>
					<div style={{ padding: 12, background: "rgba(255,255,255,0.03)", borderRadius: 8, border: "1px solid rgba(255,255,255,0.06)" }}>
						<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
							<span style={{ fontSize: 11, opacity: 0.5, textTransform: "uppercase", fontWeight: 600 }}>Resolution Notes</span>
							{!["CLOSED", "CANCELLED"].includes(wo.status) && editingField !== "resolutionNotes" && (
								<Button type="text" size="small" icon={<EditOutlined />} onClick={() => startEditing("resolutionNotes", wo.resolutionNotes ?? "")} />
							)}
						</div>
						{editingField === "resolutionNotes" ? (
							<div>
								<TextArea
									value={editValues.resolutionNotes}
									onChange={(e) => setEditValues({ ...editValues, resolutionNotes: e.target.value })}
									rows={3}
									placeholder="How was the issue resolved?"
								/>
								<div style={{ display: "flex", gap: 8, marginTop: 8, justifyContent: "flex-end" }}>
									<Button size="small" onClick={cancelEditing}>Cancel</Button>
									<Button size="small" type="primary" onClick={() => saveField("resolutionNotes")}>Save</Button>
								</div>
							</div>
						) : (
							<p style={{ margin: 0, whiteSpace: "pre-wrap" }}>{wo.resolutionNotes || <span style={{ opacity: 0.4 }}>Not documented</span>}</p>
						)}
					</div>
				</div>
			)}

			{/* Cost Items */}
			<Divider orientation="left"><DollarOutlined /> Cost Items</Divider>
			<Table
				dataSource={wo.costItems ?? []}
				rowKey="id"
				size="small"
				pagination={false}
				columns={[
					{ title: "Type", dataIndex: "costType", width: 120, render: (t: string) => <Tag>{t}</Tag> },
					{ title: "Description", dataIndex: "description", ellipsis: true },
					{ title: "Amount", dataIndex: "totalCost", width: 100, align: "right" as const, render: (v: number) => `$${Number(v).toLocaleString()}` },
					{ title: "Hours", dataIndex: "hours", width: 70, render: (v: number | null) => v ?? "—" },
					{
						title: "", width: 50,
						render: (_: unknown, record: CostItem) => (
							<Popconfirm title="Remove this cost?" onConfirm={() => handleDeleteCost(record.id)}>
								<Button type="text" danger icon={<DeleteOutlined />} size="small" />
							</Popconfirm>
						),
					},
				]}
			/>
			<Button icon={<PlusOutlined />} onClick={() => setShowCostModal(true)} style={{ marginTop: 8 }}>
				Add Cost Item
			</Button>

			{/* Triage History (if a FacilitySolve session is linked) */}
			{triageSessions.length > 0 && (
				<>
					<Divider orientation="left"><BulbOutlined /> FacilitySolve Triage History</Divider>
					{triageSessions.map((ts) => (
						<div
							key={ts.id}
							style={{
								padding: "12px 16px",
								background: "rgba(250, 140, 22, 0.04)",
								border: "1px solid rgba(250, 140, 22, 0.15)",
								borderRadius: 10,
								marginBottom: 12,
								cursor: "pointer",
							}}
							onClick={() => navigate(`/work-orders/triage/${ts.id}`)}
						>
							<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
								<strong>{ts.title}</strong>
								<Tag color={ts.status === "ESCALATED" ? "warning" : ts.status === "RESOLVED_SELF" ? "success" : "processing"}>
									{ts.status.replace("_", " ")}
								</Tag>
							</div>
							{ts.aiDiagnosis && (
								<div style={{ marginTop: 8, fontSize: 13, color: "#666" }}>
									<BulbOutlined style={{ color: "#fa8c16", marginRight: 4 }} />
									<strong>AI Diagnosis:</strong> {ts.aiDiagnosis.rootCause}
									<Tag style={{ marginLeft: 8 }} color={ts.aiDiagnosis.confidence >= 80 ? "success" : "warning"}>
										{ts.aiDiagnosis.confidence}% confidence
									</Tag>
								</div>
							)}
							{ts.resolution && (
								<div style={{ marginTop: 4, fontSize: 12, color: "#888" }}>
									{ts.resolution.summary}
								</div>
							)}
						</div>
					))}
				</>
			)}

			{/* Vendor Quotes / RFQ Section */}
			<Divider orientation="left"><DollarOutlined /> Vendor Quotes</Divider>
			{!["CLOSED", "CANCELLED"].includes(wo.status) && (
				<Button
					type="primary"
					ghost
					onClick={openQuoteModal}
					style={{ marginBottom: 16 }}
				>
					Request Quotes from Vendors
				</Button>
			)}

			{loadingQuotes ? (
				<Spin size="small" />
			) : quoteRequests.length === 0 ? (
				<p style={{ textAlign: "center", opacity: 0.5, margin: "12px 0" }}>No quote requests yet</p>
			) : (
				quoteRequests.map((qr) => (
					<div key={qr.id} style={{ marginBottom: 20, padding: 16, border: "1px solid rgba(255,255,255,0.08)", borderRadius: 12, background: "rgba(255,255,255,0.02)" }}>
						<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
							<div>
								<h4 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>{qr.title}</h4>
								<div style={{ fontSize: 12, opacity: 0.5 }}>
									Created {new Date(qr.createdAt).toLocaleDateString()} · {qr.quotes.length} vendor{qr.quotes.length !== 1 ? "s" : ""}
									{qr.deadline && ` · Due ${new Date(qr.deadline).toLocaleDateString()}`}
								</div>
							</div>
							<div style={{ display: "flex", gap: 8, alignItems: "center" }}>
								<Tag color={qr.status === "OPEN" ? "blue" : qr.status === "AWARDED" ? "green" : qr.status === "CANCELLED" ? "red" : "default"}>
									{qr.status}
								</Tag>
								{qr.status === "OPEN" && (
									<Popconfirm title="Cancel this quote request?" onConfirm={() => handleCancelQuoteRequest(qr.id)} okText="Yes" cancelText="No">
										<Button size="small" danger type="text">Cancel</Button>
									</Popconfirm>
								)}
							</div>
						</div>
						{qr.scopeOfWork && (
							<div style={{ fontSize: 13, opacity: 0.7, marginBottom: 12, padding: "8px 10px", background: "rgba(255,255,255,0.03)", borderRadius: 6 }}>
								<strong style={{ fontSize: 11, textTransform: "uppercase", opacity: 0.5 }}>Scope: </strong>{qr.scopeOfWork}
							</div>
						)}
						<Table
							dataSource={qr.quotes}
							rowKey="id"
							size="small"
							pagination={false}
							columns={[
								{
									title: "Vendor",
									dataIndex: "companyName",
									width: 200,
									render: (name: string, record: VendorQuoteWithVendor) => (
										<div>
											<div style={{ fontWeight: 500 }}>{name}</div>
											{record.trades?.length > 0 && (
												<div style={{ fontSize: 11, opacity: 0.5 }}>{record.trades.map((t) => t.name).join(", ")}</div>
											)}
										</div>
									),
								},
								{
									title: "Status",
									dataIndex: "status",
									width: 110,
									render: (s: string) => (
										<Tag color={s === "SUBMITTED" ? "blue" : s === "ACCEPTED" ? "green" : s === "PENDING" ? "default" : s === "DECLINED" ? "red" : "default"}>
											{s}
										</Tag>
									),
								},
								{
									title: "Amount",
									dataIndex: "quotedAmount",
									width: 120,
									align: "right" as const,
									render: (v: number | null) => v !== null ? `$${Number(v).toLocaleString()}` : "—",
								},
								{
									title: "Hours",
									dataIndex: "estimatedHours",
									width: 80,
									render: (v: number | null) => v ? `${v}h` : "—",
								},
								{
									title: "Days",
									dataIndex: "estimatedDays",
									width: 70,
									render: (v: number | null) => v ?? "—",
								},
								{
									title: "Notes",
									dataIndex: "proposalNotes",
									ellipsis: true,
									render: (v: string | null) => v || "—",
								},
								{
									title: "",
									width: 160,
									render: (_: unknown, record: VendorQuoteWithVendor) => {
										if (qr.status !== "OPEN") return null;
										return (
											<div style={{ display: "flex", gap: 4 }}>
												{record.status === "PENDING" && (
													<Button size="small" onClick={() => openSubmitQuoteModal(record)}>Enter Quote</Button>
												)}
												{record.status === "SUBMITTED" && (
													<Popconfirm title={`Accept quote from ${record.companyName} for $${Number(record.quotedAmount).toLocaleString()}?`} onConfirm={() => handleAcceptQuote(qr.id, record.id)} okText="Accept" cancelText="Cancel">
														<Button size="small" type="primary">Accept</Button>
													</Popconfirm>
												)}
											</div>
										);
									},
								},
							]}
						/>
					</div>
				))
			)}

			{/* FacilityLens Section */}
			{flScans.length > 0 && (
				<>
					<Divider orientation="left"><ScanOutlined /> FacilityLens</Divider>
					{flScans.map((scan) => (
						<div key={scan.id} style={{ marginBottom: 16, padding: 16, border: "1px solid #e8e8ef", borderRadius: 12, background: "#fafbff" }}>
							<div style={{ display: "flex", gap: 12, marginBottom: 12 }}>
								<img
									src={scan.primaryPhotoUrl}
									alt="Scan"
									style={{ width: 80, height: 80, objectFit: "cover", borderRadius: 8 }}
								/>
								<div>
									<h4 style={{ margin: 0, fontSize: 16 }}>
										{scan.aiIdentification?.assetType || "Asset Scan"}
									</h4>
									{scan.aiIdentification?.manufacturer && (
										<span style={{ color: "#0984e3", fontWeight: 500 }}>
											{scan.aiIdentification.manufacturer}
										</span>
									)}
									{scan.aiIdentification?.confidence !== undefined && (
										<Tag color={scan.aiIdentification.confidence >= 80 ? "green" : scan.aiIdentification.confidence >= 60 ? "gold" : "red"} style={{ marginLeft: 8 }}>
											{scan.aiIdentification.confidence}% confidence
										</Tag>
									)}
								</div>
							</div>

							{/* Selected Issues */}
							{scan.selectedIssues && scan.selectedIssues.length > 0 && (
								<div style={{ marginBottom: 12 }}>
									<strong style={{ fontSize: 13 }}><WarningOutlined /> Selected Issues:</strong>
									{scan.selectedIssues.map((iss, i) => (
										<div key={i} style={{ padding: "4px 0 4px 16px", fontSize: 13, color: "#555" }}>
											<Tag color={iss.severity === "CRITICAL" ? "red" : iss.severity === "HIGH" ? "orange" : "gold"} style={{ fontSize: 11 }}>
												{iss.severity}
											</Tag>
											<strong>{iss.title}</strong> — {iss.solutionSummary}
										</div>
									))}
								</div>
							)}

							{/* Knowledge manuals */}
							{scan.knowledge?.manualResources && scan.knowledge.manualResources.length > 0 && (
								<Collapse ghost items={[{
									key: "manuals",
									label: <span><BookOutlined /> Resource Manuals ({scan.knowledge.manualResources.length})</span>,
									children: (
										<div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
											{scan.knowledge.manualResources.map((m, i) => (
												<a key={i} href={m.url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 13 }}>
													{m.title} <Tag style={{ fontSize: 10 }}>{m.source}</Tag>
												</a>
											))}
										</div>
									),
								}]} />
							)}
						</div>
					))}

					{/* Message Thread */}
					<div style={{ margin: "12px 0 20px" }}>
						<h4 style={{ fontSize: 14, marginBottom: 8 }}>
							<MessageOutlined /> Tech-Customer Messages
						</h4>

						{/* AI-suggested questions */}
						{flSuggestions.length > 0 && (
							<div style={{ marginBottom: 12 }}>
								<span style={{ fontSize: 12, color: "#8e8ea0" }}><BulbOutlined /> Suggested questions:</span>
								<div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 4 }}>
									{flSuggestions.map((s, i) => (
										<Button
											key={i}
											size="small"
											onClick={() => setFlMsgInput(s.question)}
											style={{ fontSize: 12, borderRadius: 16 }}
										>
											{s.question}
										</Button>
									))}
								</div>
							</div>
						)}

						{/* Messages list */}
						<div style={{ maxHeight: 300, overflowY: "auto", marginBottom: 10, border: "1px solid #e8e8ef", borderRadius: 8, padding: 10, background: "#fff" }}>
							{flMessages.length === 0 ? (
								<p style={{ textAlign: "center", color: "#aaa", fontSize: 13, margin: 16 }}>No messages yet</p>
							) : (
								flMessages.map((msg) => (
									<div
										key={msg.id}
										style={{
											marginBottom: 8,
											padding: "8px 12px",
											borderRadius: 10,
											background: msg.senderRole === "TECHNICIAN" ? "#e8f0fe" :
												msg.senderRole === "AI_ASSISTANT" ? "#f0f0f5" : "#fff",
											border: msg.senderRole === "CUSTOMER" ? "1px solid #e8e8ef" : "none",
										}}
									>
										<div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, opacity: 0.6, marginBottom: 2 }}>
											<span>{msg.senderRole === "AI_ASSISTANT" ? "AI Assistant" : msg.senderRole.toLowerCase()}</span>
											<span>{new Date(msg.createdAt).toLocaleString()}</span>
										</div>
										<p style={{ margin: 0, fontSize: 14 }}>{msg.content}</p>
									</div>
								))
							)}
						</div>

						{/* Send message */}
						<div style={{ display: "flex", gap: 8 }}>
							<TextArea
								value={flMsgInput}
								onChange={(e) => setFlMsgInput(e.target.value)}
								placeholder="Ask the customer a question..."
								rows={2}
								style={{ flex: 1 }}
								onPressEnter={(e) => { if (!e.shiftKey) { e.preventDefault(); handleFlSend(); } }}
							/>
							<Button
								type="primary"
								icon={<SendOutlined />}
								loading={flSending}
								onClick={handleFlSend}
								disabled={!flMsgInput.trim()}
							>
								Send
							</Button>
						</div>
					</div>
				</>
			)}

			{/* Activity Log */}
			<Divider orientation="left">Activity Log</Divider>
			{wo.activityLog && wo.activityLog.length > 0 ? (
				<Timeline
					items={wo.activityLog.map((entry) => ({
						color: entry.isSystemEvent ? "gray" : "blue",
						children: (
							<div>
								<div style={{ display: "flex", justifyContent: "space-between" }}>
									<strong>{entry.userName ?? "System"}</strong>
									<span style={{ fontSize: 11, opacity: 0.5 }}>{formatDate(entry.createdAt)}</span>
								</div>
								{entry.comment ? (
									<p style={{ margin: "4px 0 0", padding: "6px 10px", background: "rgba(255,255,255,0.04)", borderRadius: 6 }}>{entry.comment}</p>
								) : (
									<span style={{ fontSize: 12, opacity: 0.6 }}>
										{entry.action.replace(/_/g, " ").toLowerCase()}
										{(entry.details as any)?.from && <> — {(entry.details as any).from} → {(entry.details as any).to}</>}
									</span>
								)}
							</div>
						),
					}))}
				/>
			) : (
				<p style={{ textAlign: "center", opacity: 0.5 }}>No activity yet</p>
			)}

			{/* Add Comment */}
			<div style={{ display: "flex", gap: 8, marginTop: 12 }}>
				<TextArea value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Add a comment..." rows={2} style={{ flex: 1 }} />
				<Button type="primary" icon={<SendOutlined />} loading={submitting} onClick={handleComment} disabled={!comment.trim()}>
					Send
				</Button>
			</div>

			{/* Cost Modal */}
			<Modal
				open={showCostModal}
				onCancel={() => setShowCostModal(false)}
				title="Add Cost Item"
				onOk={handleAddCost}
				okText="Add"
			>
				<div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
					<div>
						<label>Type</label>
						<Select value={costForm.costType} onChange={(v) => setCostForm({ ...costForm, costType: v })} style={{ width: "100%" }}
							options={[
								{ value: "LABOR", label: "Labor" },
								{ value: "PARTS", label: "Parts" },
								{ value: "VENDOR_INVOICE", label: "Vendor Invoice" },
								{ value: "OTHER", label: "Other" },
							]}
						/>
					</div>
					<div>
						<label>Description</label>
						<Input value={costForm.description} onChange={(e) => setCostForm({ ...costForm, description: e.target.value })} />
					</div>
					<div style={{ display: "flex", gap: 12 }}>
						<div style={{ flex: 1 }}>
							<label>Amount ($)</label>
							<InputNumber value={costForm.totalCost} onChange={(v) => setCostForm({ ...costForm, totalCost: v ?? 0 })} style={{ width: "100%" }} min={0} />
						</div>
						{costForm.costType === "LABOR" && (
							<div style={{ flex: 1 }}>
								<label>Hours</label>
								<InputNumber value={costForm.hours} onChange={(v) => setCostForm({ ...costForm, hours: v ?? 0 })} style={{ width: "100%" }} min={0} />
							</div>
						)}
					</div>
				</div>
			</Modal>

			{/* Complete Work Order Modal */}
			<Modal
				open={showCompleteModal}
				onCancel={() => setShowCompleteModal(false)}
				title="Complete Work Order"
				onOk={handleCompleteSubmit}
				okText="Mark as Completed"
				confirmLoading={submitting}
				width={560}
			>
				<p style={{ color: "#666", marginBottom: 16 }}>
					Document the resolution before marking this work order as complete.
				</p>
				<div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
					<div>
						<label style={{ display: "block", fontWeight: 600, marginBottom: 6 }}>
							Root Cause <span style={{ fontWeight: 400, color: "#999" }}>(optional)</span>
						</label>
						<TextArea
							value={completionForm.rootCause}
							onChange={(e) => setCompletionForm({ ...completionForm, rootCause: e.target.value })}
							placeholder="What caused the issue? e.g., Worn compressor bearings, clogged drain line..."
							rows={3}
						/>
					</div>
					<div>
						<label style={{ display: "block", fontWeight: 600, marginBottom: 6 }}>
							Resolution Notes <span style={{ color: "#ff4d4f" }}>*</span>
						</label>
						<TextArea
							value={completionForm.resolutionNotes}
							onChange={(e) => setCompletionForm({ ...completionForm, resolutionNotes: e.target.value })}
							placeholder="How was the issue resolved? e.g., Replaced compressor bearings and recharged refrigerant to spec..."
							rows={4}
						/>
					</div>
				</div>
			</Modal>

			{/* Assign Modal */}
			<Modal
				open={showAssignModal}
				onCancel={() => setShowAssignModal(false)}
				title="Assign Work Order"
				onOk={handleAssign}
				okText="Assign"
				confirmLoading={submitting}
			>
				<div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
					<div>
						<label style={{ display: "block", fontWeight: 600, marginBottom: 6 }}>Assign To</label>
						<Select
							value={assignType}
							onChange={(v: "user" | "vendor" | "fl_vendor") => {
								setAssignType(v);
								setAssignUserId(null);
								setAssignVendorId(null);
								setAssignFlOrgVendorId(null);
							}}
							style={{ width: "100%" }}
							options={[
								{ value: "user", label: "Internal Team Member" },
								{ value: "vendor", label: "External Vendor" },
								{ value: "fl_vendor", label: "FacilityLink Vendor Network" },
							]}
						/>
					</div>
					{assignType === "user" && (
						<div>
							<label style={{ display: "block", fontWeight: 600, marginBottom: 6 }}>Team Member</label>
							<Select
								showSearch
								placeholder="Select a team member..."
								value={assignUserId}
								onChange={setAssignUserId}
								loading={loadingAssignees}
								style={{ width: "100%" }}
								filterOption={(input, option) =>
									(option?.label as string ?? "").toLowerCase().includes(input.toLowerCase())
								}
								options={orgUsers.map((u) => ({
									value: u.id,
									label: u.label,
								}))}
							/>
						</div>
					)}
					{assignType === "vendor" && (
						<div>
							<label style={{ display: "block", fontWeight: 600, marginBottom: 6 }}>Vendor</label>
							<Select
								showSearch
								placeholder="Select a vendor..."
								value={assignVendorId}
								onChange={setAssignVendorId}
								loading={loadingAssignees}
								style={{ width: "100%" }}
								filterOption={(input, option) =>
									(option?.label as string ?? "").toLowerCase().includes(input.toLowerCase())
								}
								options={orgVendors.map((v) => ({
									value: v.id,
									label: v.name,
								}))}
							/>
						</div>
					)}
					{assignType === "fl_vendor" && (
						<div>
							<label style={{ display: "block", fontWeight: 600, marginBottom: 6 }}>FacilityLink Vendor</label>
							<Select
								showSearch
								placeholder="Select from your vendor network..."
								value={assignFlOrgVendorId}
								onChange={setAssignFlOrgVendorId}
								loading={loadingAssignees}
								style={{ width: "100%" }}
								filterOption={(input, option) =>
									(option?.label as string ?? "").toLowerCase().includes(input.toLowerCase())
								}
								options={flVendors.map((v) => ({
									value: v.id,
									label: `${v.companyName}${v.trades?.length ? ` — ${v.trades.map((t) => t.name).join(", ")}` : ""}`,
								}))}
							/>
						</div>
					)}
				</div>
			</Modal>

			{/* Request Quotes Modal */}
			<Modal
				open={showQuoteModal}
				onCancel={() => setShowQuoteModal(false)}
				title="Request Quotes from Vendors"
				onOk={handleCreateQuoteRequest}
				okText="Send Quote Requests"
				confirmLoading={submitting}
				width={600}
			>
				<p style={{ color: "#666", marginBottom: 16 }}>
					Select vendors from your FacilityLink network to request competitive quotes for this work order.
				</p>
				<div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
					<div>
						<label style={{ display: "block", fontWeight: 600, marginBottom: 6 }}>Title</label>
						<Input value={quoteForm.title} onChange={(e) => setQuoteForm({ ...quoteForm, title: e.target.value })} placeholder="Quote request title" />
					</div>
					<div>
						<label style={{ display: "block", fontWeight: 600, marginBottom: 6 }}>Description</label>
						<TextArea value={quoteForm.description} onChange={(e) => setQuoteForm({ ...quoteForm, description: e.target.value })} rows={2} placeholder="Brief description of the work needed" />
					</div>
					<div>
						<label style={{ display: "block", fontWeight: 600, marginBottom: 6 }}>Scope of Work</label>
						<TextArea value={quoteForm.scopeOfWork} onChange={(e) => setQuoteForm({ ...quoteForm, scopeOfWork: e.target.value })} rows={3} placeholder="Detailed scope: what exactly needs to be done?" />
					</div>
					<div>
						<label style={{ display: "block", fontWeight: 600, marginBottom: 6 }}>Response Deadline</label>
						<DatePicker
							value={quoteForm.deadline ? dayjs(quoteForm.deadline) : null}
							onChange={(d) => setQuoteForm({ ...quoteForm, deadline: d?.toISOString() ?? "" })}
							style={{ width: "100%" }}
							format="MMM D, YYYY"
						/>
					</div>
					<div>
						<label style={{ display: "block", fontWeight: 600, marginBottom: 6 }}>
							Select Vendors <span style={{ fontWeight: 400, color: "#999" }}>({quoteForm.vendorOrgIds.length} selected)</span>
						</label>
						<div style={{ maxHeight: 200, overflowY: "auto", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, padding: 8 }}>
							{flVendors.length === 0 ? (
								<p style={{ textAlign: "center", color: "#999", margin: 16 }}>No vendors in your network. Add vendors via FacilityLink first.</p>
							) : (
								flVendors.map((v) => (
									<div key={v.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 4px", borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
										<Checkbox
											checked={quoteForm.vendorOrgIds.includes(v.id)}
											onChange={(e) => {
												const ids = e.target.checked
													? [...quoteForm.vendorOrgIds, v.id]
													: quoteForm.vendorOrgIds.filter((id) => id !== v.id);
												setQuoteForm({ ...quoteForm, vendorOrgIds: ids });
											}}
										/>
										<div>
											<div style={{ fontWeight: 500 }}>{v.companyName}</div>
											{v.trades?.length > 0 && (
												<div style={{ fontSize: 11, opacity: 0.6 }}>{v.trades.map((t) => t.name).join(", ")}</div>
											)}
										</div>
									</div>
								))
							)}
						</div>
					</div>
				</div>
			</Modal>

			{/* Submit Quote Modal (for demo: simulates vendor response) */}
			<Modal
				open={showSubmitQuoteModal}
				onCancel={() => setShowSubmitQuoteModal(false)}
				title={`Submit Quote — ${submitQuoteTarget?.companyName ?? ""}`}
				onOk={handleSubmitQuote}
				okText="Submit Quote"
				confirmLoading={submitting}
				width={500}
			>
				<p style={{ color: "#666", marginBottom: 16 }}>
					Enter the vendor's quote details (for demo purposes, you can fill this in on behalf of the vendor).
				</p>
				<div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
					<div>
						<label style={{ display: "block", fontWeight: 600, marginBottom: 6 }}>Quoted Amount ($) <span style={{ color: "#ff4d4f" }}>*</span></label>
						<InputNumber value={submitQuoteForm.quotedAmount} onChange={(v) => setSubmitQuoteForm({ ...submitQuoteForm, quotedAmount: v ?? 0 })} style={{ width: "100%" }} min={0} prefix="$" />
					</div>
					<div style={{ display: "flex", gap: 12 }}>
						<div style={{ flex: 1 }}>
							<label style={{ display: "block", fontWeight: 600, marginBottom: 6 }}>Estimated Hours</label>
							<InputNumber value={submitQuoteForm.estimatedHours} onChange={(v) => setSubmitQuoteForm({ ...submitQuoteForm, estimatedHours: v ?? 0 })} style={{ width: "100%" }} min={0} />
						</div>
						<div style={{ flex: 1 }}>
							<label style={{ display: "block", fontWeight: 600, marginBottom: 6 }}>Estimated Days</label>
							<InputNumber value={submitQuoteForm.estimatedDays} onChange={(v) => setSubmitQuoteForm({ ...submitQuoteForm, estimatedDays: v ?? 0 })} style={{ width: "100%" }} min={0} />
						</div>
					</div>
					<div>
						<label style={{ display: "block", fontWeight: 600, marginBottom: 6 }}>Proposal Notes</label>
						<TextArea value={submitQuoteForm.proposalNotes} onChange={(e) => setSubmitQuoteForm({ ...submitQuoteForm, proposalNotes: e.target.value })} rows={3} placeholder="Additional details, approach, materials needed..." />
					</div>
				</div>
			</Modal>
		</div>
	);
};

export { WorkOrderDetailPage };
